import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { getPincodes } from "../../service/Locations/locationsService";
import { notifyError } from "../../components/ToastContainer/Toast/Toast";

// eslint-disable-next-line @typescript-eslint/no-unused-vars
interface PincodeResponse {
  status: boolean;
  message: string;
  pincodes: string[];
}

interface PincodeSlice {
  pincodes: string[];
  loading: boolean;
  error: string | null;
}

const initialState: PincodeSlice = {
  pincodes: [],
  loading: false,
  error: null,
};

//Simple in-memory cache
const pincodeCache: Record<string, string[]> = {};

// Async thunk to fetch pincodes with cache
export const fetchPincodes = createAsyncThunk(
  "pincodes/fetchPincodes",
  async (
    { cityName }: { cityName: string },
    { rejectWithValue }
  ) => {
    const key = cityName.toUpperCase();

    //Return from cache if exists
    if (pincodeCache[key]) {
      return pincodeCache[key];
    }

    try {
      const response = await getPincodes(key);

      if (!response.data?.data) {
        throw new Error("No data found for the given city.");
      }

      pincodeCache[key] = response.data.data;

      return response.data.data;
    } catch (error: any) {
      notifyError(
        error?.response?.data.message ||
        "Failed to fetch pincodes. Please try again."
      );
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch pincodes. Please try again."
      );
    }
  }
);

const pincodeSlice = createSlice({
  name: "pincodes",
  initialState,
  reducers: {
    resetPincodes: (state) => {
      state.pincodes = [];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPincodes.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPincodes.fulfilled, (state, action) => {
        state.loading = false;
        state.pincodes = action.payload;
      })
      .addCase(fetchPincodes.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { resetPincodes } = pincodeSlice.actions;
export default pincodeSlice.reducer;
